import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Send, X, Trash2, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseRecipients = (s) =>
  (s || '')
    .split(/[,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const quoteOriginal = (message) => {
  if (!message) return '';
  const dateStr = message.date ? new Date(message.date).toLocaleString() : '';
  const from = message.from || message.from_email || '';
  const quoted = (message.text || (message.html ? '(HTML content)' : ''))
    .split('\n')
    .map((l) => `> ${l}`)
    .join('\n');
  return `\n\nOn ${dateStr}, ${from} wrote:\n${quoted}\n`;
};

/**
 * Gmail-style inline reply/forward composer rendered inside the open thread.
 * Auto-saves to a real Gmail draft (debounced) and sends via the draft when one exists.
 */
export default function InlineReplyComposer({ mode = 'reply', replyTo, mailbox = 'shared', existingDraft = null, onSent, onClose, onDraftSaved }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [draftId, setDraftId] = useState(existingDraft?.draft_id || null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [sending, setSending] = useState(false);

  const saveTimer = useRef(null);
  const dirtyRef = useRef(false);
  const bodyRef = useRef(null);

  // Prefill once on mount based on mode + replyTo (or an existing draft).
  useEffect(() => {
    if (existingDraft) {
      setTo(existingDraft.to || '');
      setCc(existingDraft.cc || '');
      setShowCc(!!existingDraft.cc);
      setSubject(existingDraft.subject || '');
      setBody(existingDraft.body_text || '');
      return;
    }
    if (mode === 'reply' && replyTo) {
      setTo(replyTo.from_email || replyTo.from || '');
      setSubject(replyTo.subject?.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`);
      setBody(quoteOriginal(replyTo));
    } else if (mode === 'replyAll' && replyTo) {
      setTo(replyTo.from_email || replyTo.from || '');
      const others = [replyTo.to, replyTo.cc].filter(Boolean).join(', ');
      if (others) { setCc(others); setShowCc(true); }
      setSubject(replyTo.subject?.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`);
      setBody(quoteOriginal(replyTo));
    } else if (mode === 'forward' && replyTo) {
      setSubject(replyTo.subject?.startsWith('Fwd:') ? replyTo.subject : `Fwd: ${replyTo.subject || ''}`);
      const fwd = `\n\n---------- Forwarded message ----------\nFrom: ${replyTo.from || ''}\nDate: ${replyTo.date ? new Date(replyTo.date).toLocaleString() : ''}\nSubject: ${replyTo.subject || ''}\nTo: ${replyTo.to || ''}\n\n${replyTo.text || ''}\n`;
      setBody(fwd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildDraftPayload = useCallback(() => ({
    draft_id: draftId || undefined,
    to: parseRecipients(to),
    cc: showCc && cc ? parseRecipients(cc) : null,
    subject,
    mailbox,
    body_text: body,
    body_html: `<div style="white-space:pre-wrap;font-family:Arial,Helvetica,sans-serif;">${body
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`,
    thread_id: replyTo?.thread_id,
    in_reply_to_message_id: replyTo?.rfc_message_id || undefined,
    references: replyTo?.references || undefined,
  }), [draftId, to, cc, showCc, subject, body, mailbox, replyTo]);

  const saveDraft = useCallback(async () => {
    if (!dirtyRef.current) return;
    setSaveState('saving');
    try {
      const res = await axios.post(`${API}/communications/drafts`, buildDraftPayload());
      if (res.data?.draft_id) {
        const isNew = !draftId;
        setDraftId(res.data.draft_id);
        if (isNew) onDraftSaved?.(); // refresh thread list so the "Draft" badge appears
      }
      dirtyRef.current = false;
      setSaveState('saved');
    } catch (e) {
      setSaveState('idle');
    }
  }, [buildDraftPayload, draftId, onDraftSaved]);

  // Debounced auto-save whenever the editable fields change.
  useEffect(() => {
    dirtyRef.current = true;
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(saveDraft, 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, subject, body, showCc]);

  const handleSend = async () => {
    const recipients = parseRecipients(to);
    if (recipients.length === 0) { toast.error('Add at least one recipient'); return; }
    if (!body.trim() && !window.confirm('Send without a body?')) return;
    setSending(true);
    try {
      // Make sure the latest edits are in the draft, then send the draft.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      dirtyRef.current = true;
      const saveRes = await axios.post(`${API}/communications/drafts`, buildDraftPayload());
      const id = saveRes.data?.draft_id || draftId;
      let res;
      if (id) {
        res = await axios.post(`${API}/communications/drafts/${id}/send?mailbox=${mailbox}`);
      } else {
        res = await axios.post(`${API}/communications/messages/send`, buildDraftPayload());
      }
      toast.success('Email sent');
      onSent?.(res.data);
    } catch (e) {
      toast.error(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (draftId) {
      try { await axios.delete(`${API}/communications/drafts/${draftId}?mailbox=${mailbox}`); } catch (e) { /* ignore */ }
    }
    onClose?.({ discarded: true });
  };

  const handleClose = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (dirtyRef.current) { try { await saveDraft(); } catch (e) { /* ignore */ } }
    onClose?.({ saved: !!draftId });
  };

  return (
    <div className="border-t bg-white" data-testid="inline-reply-composer">
      <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/20">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold capitalize">{mode === 'replyAll' ? 'Reply all' : mode}</span>
          <span className="text-muted-foreground" data-testid="draft-save-state">
            {saveState === 'saving' && <span className="inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Saving…</span>}
            {saveState === 'saved' && <span className="inline-flex items-center gap-1 text-green-600"><Check size={11} /> Draft saved</span>}
          </span>
        </div>
        <button onClick={handleClose} className="text-muted-foreground hover:text-foreground p-1" title="Minimize (keeps draft)" data-testid="inline-reply-close">
          <X size={15} />
        </button>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-12">To</span>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" className="h-8 text-sm" data-testid="inline-reply-to" />
          {!showCc && (
            <button onClick={() => setShowCc(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="inline-reply-add-cc">Cc</button>
          )}
        </div>
        {showCc && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-12">Cc</span>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@example.com" className="h-8 text-sm" data-testid="inline-reply-cc" />
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-12">Subject</span>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-8 text-sm" data-testid="inline-reply-subject" />
        </div>
        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          className="w-full resize-y text-sm border rounded-md p-2 outline-none focus:ring-1 focus:ring-primary"
          placeholder="Write your reply…"
          data-testid="inline-reply-body"
        />
        <div className="flex items-center justify-between">
          <Button size="sm" onClick={handleSend} disabled={sending} className="gap-1" data-testid="inline-reply-send">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
          </Button>
          <button onClick={handleDiscard} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600" data-testid="inline-reply-discard">
            <Trash2 size={13} /> Discard draft
          </button>
        </div>
      </div>
    </div>
  );
}
